import { useEffect } from 'react';
import { Edit, useForm } from '@refinedev/antd';
import { useCan } from '@refinedev/core';
import { Navigate, useParams } from 'react-router-dom';
import {
  Form,
  Input,
  ColorPicker,
  Descriptions,
  Spin,
  Result,
  Alert,
} from 'antd';
import type { HttpError } from '@refinedev/core';
import type { Company } from './list';
import { COMPANIES_META } from './constants';
import { buildCompanyPatchPayload, type CompanyEditFormValues } from './buildCompanyPatchPayload';
import { useCanEditCompany } from './api/editableCompaniesClient';

type FormValues = CompanyEditFormValues;

export const CompanyEditPage: React.FC = () => {
  // Gate on BOTH read and write. The admin-app policy is "all company pages
  // require read", so a user lacking read must never reach the edit surface
  // — even if a future policy change granted write without read.
  const { data: canRead, isLoading: canReadLoading } = useCan({
    resource: 'companies',
    action: 'show',
  });
  const { data: canEditPermission, isLoading: canEditPermissionLoading } = useCan({
    resource: 'companies',
    action: 'edit',
  });
  // SF-4668: staff with a company list may only edit the companies on it; others are sent to the
  // read-only detail page, like users without the edit permission.
  const { id: routeId } = useParams<{ id: string }>();
  const { canEditCompany, isLoading: scopeLoading } = useCanEditCompany(routeId);
  const canEditLoading = canEditPermissionLoading || scopeLoading;
  const canEdit = {
    can: canEditPermission?.can === true && canEditCompany,
    reason:
      canEditPermission?.can === true && !canEditCompany
        ? 'This company is not on your list of companies, so you can only view it.'
        : canEditPermission?.reason,
  };

  const { formProps, saveButtonProps, queryResult, form } = useForm<Company, HttpError, FormValues>({
    resource: 'companies',
    action: 'edit',
    meta: COMPANIES_META,
    redirect: 'show',
    // Gate the data-fetch query on the read permission — consistent with
    // CompanyDetailPage and CompaniesListPage — so we never fire an API
    // request (and a spurious 403 notification) before the permission check
    // resolves for unauthorized users.
    queryOptions: { enabled: canRead?.can === true, refetchOnWindowFocus: false },
    // Only send whitelisted fields to PATCH to match sf-api's strict rejection
    // of unknown fields (SF-3475 returns 400 for any non-whitelisted key).
    // The colour picker returns a Color object — coerce to hex string.
    submitOnEnter: false,
    // Refine's default toast shows axios' "Request failed with status code 400";
    // show sf-api's reason instead (e.g. "privacy_link must be a valid URL").
    errorNotification: (error) => {
      const e = error as { response?: { data?: { message?: string } }; message?: string } | undefined;
      return {
        type: 'error',
        message: 'Could not save the company',
        description: e?.response?.data?.message || e?.message || 'Please try again.',
      };
    },
  });

  const company = queryResult?.data?.data;
  // Keep form in sync once data arrives. useForm populates automatically, but
  // the ColorPicker value needs the raw hex string passed through.
  useEffect(() => {
    if (company && form) {
      form.setFieldsValue({
        name: company.name,
        color: company.color,
        privacy_link: company.privacy_link,
      });
    }
  }, [company, form]);

  if (canReadLoading || canEditLoading || (canRead?.can === true && queryResult?.isLoading)) {
    return <Spin style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }} />;
  }

  if (!canRead?.can) {
    return (
      <Result
        status="403"
        title="Not Authorized"
        subTitle={canRead?.reason ?? 'You do not have permission to view companies.'}
      />
    );
  }

  if (!canEdit?.can) {
    // Users without write perm get redirected to read-only detail rather than
    // staring at a dead edit form.
    if (company?._id || company?.id) {
      return <Navigate to={`/companies/${company._id || company.id}`} replace />;
    }
    return (
      <Result
        status="403"
        title="Not Authorized"
        subTitle={canEdit?.reason ?? 'You do not have permission to edit companies.'}
      />
    );
  }

  return (
    <Edit saveButtonProps={saveButtonProps} title={`Edit ${company?.name ?? 'Company'}`}>
      <Alert
        type="info"
        showIcon
        message="Only a subset of fields is editable"
        description="Internal name and creation date are managed upstream and cannot be changed here. Active, account type, sales channel and feature flags are changed on the company page."
        style={{ marginBottom: 16 }}
      />
      <Form
        {...formProps}
        layout="vertical"
        onFinish={(values) => {
          const payload = buildCompanyPatchPayload(values as FormValues);
          return formProps.onFinish?.(payload as unknown as FormValues);
        }}
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Name is required' }]}
        >
          <Input placeholder="Company name" />
        </Form.Item>

        <Form.Item label="Brand Colour" name="color">
          <ColorPicker showText format="hex" />
        </Form.Item>

        <Form.Item
          label="Privacy Link"
          name="privacy_link"
          rules={[
            {
              type: 'url',
              message: 'Enter a valid URL (https://...)',
            },
          ]}
        >
          <Input placeholder="https://example.com/privacy" />
        </Form.Item>

      </Form>

      <Descriptions title="Read-only" bordered column={1} style={{ marginTop: 24 }}>
        <Descriptions.Item label="Internal Name">
          {company?.internal_name || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Created At">
          {company?.createdAt ? new Date(company.createdAt).toLocaleString() : '—'}
        </Descriptions.Item>
      </Descriptions>
    </Edit>
  );
};
